import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Star, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/** Star selector 1–10 (compact: 10 half-width stars) */
function RatingBar({ value, onChange, testId }) {
  const [hover, setHover] = useState(0);
  const display = hover || value || 0;
  return (
    <div
      className="flex items-center gap-0.5"
      onMouseLeave={() => setHover(0)}
      data-testid={testId}
    >
      {Array.from({ length: 10 }).map((_, i) => {
        const n = i + 1;
        const active = display >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            aria-label={`${n} stelle`}
            className={`h-7 w-7 rounded-md flex items-center justify-center transition ${
              active ? "text-amber-400" : "text-slate-300 hover:text-slate-400"
            }`}
          >
            <Star size={18} fill={active ? "currentColor" : "none"} />
          </button>
        );
      })}
      <span
        className={`ml-2 text-sm font-heading font-semibold w-6 text-right ${
          value ? "text-slate-900" : "text-slate-400"
        }`}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value || "—"}
      </span>
    </div>
  );
}

export default function PlayerRatingsDialog({
  open,
  onOpenChange,
  convocation,
  players,
  onSaved,
}) {
  const [entries, setEntries] = useState({}); // player_id -> { rating, note }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const teamId = convocation?.team_id;
  const matchId = convocation?.id;

  // Load existing ratings for the match and seed the dialog
  useEffect(() => {
    if (!open || !teamId || !matchId) return;
    setLoading(true);
    api
      .get(`/teams/${teamId}/ratings?match_id=${matchId}`)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach((r) => {
          map[r.player_id] = { rating: r.rating, note: r.note || "" };
        });
        setEntries(map);
      })
      .catch(() => setEntries({}))
      .finally(() => setLoading(false));
  }, [open, teamId, matchId]);

  if (!convocation) return null;

  const convocati = (convocation.player_ids || [])
    .map((pid) => players.find((p) => p.id === pid))
    .filter(Boolean);

  const update = (pid, patch) =>
    setEntries((e) => ({ ...e, [pid]: { rating: 0, note: "", ...(e[pid] || {}), ...patch } }));

  const submit = async () => {
    const payload = {
      match_id: matchId,
      ratings: Object.entries(entries)
        .filter(([, v]) => Number(v?.rating) > 0)
        .map(([player_id, v]) => ({
          player_id,
          rating: Number(v.rating),
          note: (v.note || "").trim() || null,
        })),
    };
    if (payload.ratings.length === 0) {
      toast.error("Assegna almeno un voto prima di salvare");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/teams/${teamId}/ratings`, payload);
      toast.success(`${payload.ratings.length} valutazioni salvate`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="player-ratings-dialog"
      >
        <DialogHeader>
          <DialogTitle>Valutazione giocatori</DialogTitle>
          <DialogDescription>
            {convocation.title} · {convocation.match_date}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-400">
            <Loader2 size={20} className="animate-spin mx-auto" />
          </div>
        ) : convocati.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm">
            Nessun giocatore convocato per questa partita.
          </div>
        ) : (
          <div className="space-y-4">
            {convocati.map((p) => {
              const e = entries[p.id] || { rating: 0, note: "" };
              return (
                <div
                  key={p.id}
                  className="p-4 rounded-xl border border-slate-200 bg-white"
                  data-testid={`rating-row-${p.id}`}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-heading font-semibold">
                        {p.first_name} {p.last_name}
                        {p.jersey_number ? (
                          <span className="ml-2 text-xs text-slate-500">
                            #{p.jersey_number}
                          </span>
                        ) : null}
                      </div>
                      {p.role && (
                        <div className="text-xs text-slate-500 mt-0.5">{p.role}</div>
                      )}
                    </div>
                    <RatingBar
                      value={e.rating}
                      onChange={(v) => update(p.id, { rating: v })}
                      testId={`rating-stars-${p.id}`}
                    />
                  </div>
                  <Textarea
                    placeholder="Nota (facoltativa) — es. Ottimo pressing, ma perde qualche palla in impostazione."
                    value={e.note}
                    onChange={(ev) => update(p.id, { note: ev.target.value })}
                    className="mt-3 text-sm"
                    rows={2}
                    data-testid={`rating-note-${p.id}`}
                  />
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="ratings-cancel-btn"
          >
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={saving || convocati.length === 0}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="ratings-save-btn"
          >
            {saving ? "Salvataggio…" : "Salva valutazioni"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
